import React, { useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import Cropper from "react-cropper";
import "cropperjs/dist/cropper.css";
import { useDispatch } from "react-redux";
import { handleUploadStationLogo, handleUploadProfilePhoto } from "../../store/slices/authSlice";
import ImageToPng from "../../helpers/ImageToPng";
import loadImage from "../../helpers/loadImage";

const CropperModalAdmin = ({ heading = "Logo", show, onClose, image, onCrop ,aspectRatio = 1 }) => {
    const cropperRef = useRef(null);
    const dispatch = useDispatch();
    const [progress, setProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    
    // Determine which upload function to use based on heading
    const uploadFunction = heading === "Profile Photo" ? handleUploadProfilePhoto : handleUploadStationLogo;

    const getProcessedImages = async () => {
        try {
            if (!cropperRef.current || !cropperRef.current.cropper) {
                throw new Error("Cropper is not initialized.");
            }

            const cropper = cropperRef.current.cropper;

            // Convert original image
            const imageObject = await loadImage(image);
            const originalImage = await ImageToPng(imageObject);

            // Get cropped image as a Blob
            const croppedCanvas = cropper.getCroppedCanvas({
                maxWidth: 500,
                maxHeight: 500,
                imageSmoothingEnabled: true,
                imageSmoothingQuality: "high"
            });
            if (!croppedCanvas) {
                throw new Error("Failed to generate cropped canvas.");
            }

            const croppedBlob = await new Promise((resolve) =>
                croppedCanvas.toBlob(resolve, "image/png")
            );

            // Convert Blobs to File objects
            const originalFile = new File([originalImage.encoded], "original-image.png", { type: "image/png" });
            const croppedFile = new File([croppedBlob], "cropped-image.png", { type: "image/png" });

            return [originalFile, croppedFile];
        } catch (error) {
            console.error("Error processing images:", error);
            return [];
        }
    };


    const handleCropAndUpload = async () => {
        setIsUploading(true);
        setProgress(10);

        try {
            const files = await getProcessedImages(); // Get both images
            let { payload } = await dispatch(uploadFunction({ files, setProgress })); // Upload both images
            setIsUploading(false);

            if (payload) {
                onCrop(payload); // Pass the uploaded image URL to parent
            } else {
                console.error("Upload successful but no URL found in response.");
            }
        } catch (error) {
            console.error("Upload failed:", error);
            setIsUploading(false);
        }
    };

    return (
        <Modal show={show} size="lg" onHide={onClose} centered backdrop="static" keyboard={false} style={{
            background: "black"
        }}>
            <div>
                <div className="adjust-banner-container">
                    <div className="adjust-banner-title">Adjust {heading}</div>
                    <p className="adjust-banner-description">
                        {heading === "Profile Photo" 
                            ? "Ensure that your original image is at least 500px in width and height. Crop your image to show your profile clearly."
                            : "Ensure that your original image is at least 1024px in width and height. Leave as little empty space around your logo as possible. Supply an image, which looks great against a bright background, as it will improve your station's look on the application"
                        }
                    </p>
                </div>
                {image && (
                    <div style={{ width: "70%", margin: "auto" }}>
                        <div className="ReactCrop--container">
                            <Cropper
                                src={image}
                                style={{ width: "100%", height: "auto", backgroundColor: "#fff" }}
                                aspectRatio={aspectRatio}
                                viewMode={1}
                                guides={true}
                                ref={cropperRef}
                                dragMode="crop"
                                cropBoxResizable={true}
                                autoCropArea={1}
                                zoomOnWheel={false}
                                zoomOnTouch={false}
                            />
                        </div>
                    </div>
                )}
            </div>
            <div className="modal-footer">
                <button type="button" className="cancel-btn" onClick={onClose} disabled={isUploading}>
                    Cancel
                </button>
                <button type="button" className="upload-btn" onClick={handleCropAndUpload} disabled={isUploading}>
                    {isUploading ? `Uploading... ${progress}%` : "Crop and Upload"}
                </button>
            </div>
        </Modal>
    );
};

export default CropperModalAdmin;